import React, { useEffect, useState } from 'react';
import { useFormularioRespostas } from '@/hooks/useFormularioRespostas';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft, MapPin, FileText, Calendar, User } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface FormularioRespostaDetalhesProps {
  respostaId: string;
  onClose: () => void;
  onBack?: () => void;
}

export function FormularioRespostaDetalhes({
  respostaId,
  onClose,
  onBack,
}: FormularioRespostaDetalhesProps) {
  const { getRespostaDetalhes } = useFormularioRespostas();
  const [detalhes, setDetalhes] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadDetalhes = async () => {
      try {
        const data = await getRespostaDetalhes(respostaId);
        setDetalhes(data);
      } catch (error) {
        console.error('Erro ao carregar detalhes:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadDetalhes();
  }, [respostaId]);

  const getStatusBadge = (status: string) => {
    const variants: Record<string, { variant: any; label: string }> = {
      rascunho: { variant: 'secondary', label: 'Rascunho' },
      concluido: { variant: 'default', label: 'Concluído' },
      validado: { variant: 'default', label: 'Validado' },
      rejeitado: { variant: 'destructive', label: 'Rejeitado' },
    };
    const config = variants[status] || { variant: 'secondary', label: status };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const renderValorCampo = (campo: any) => {
    if (campo.arquivos && campo.arquivos.length > 0) {
      return (
        <div className="space-y-2">
          {campo.arquivos.map((arquivo: string, idx: number) => (
            <a
              key={idx}
              href={arquivo}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline flex items-center gap-2"
            >
              <FileText className="h-4 w-4" />
              Arquivo {idx + 1}
            </a>
          ))}
        </div>
      );
    }

    if (campo.valor_json) {
      return <pre className="text-sm bg-muted p-2 rounded">{JSON.stringify(campo.valor_json, null, 2)}</pre>;
    }

    return <p className="text-sm">{campo.valor || '-'}</p>;
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center">
        <p>Carregando detalhes...</p>
      </div>
    );
  }

  if (!detalhes) {
    return (
      <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center">
        <p>Erro ao carregar detalhes da resposta</p>
      </div>
    );
  }

  const { resposta, campos } = detalhes;

  return (
    <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center">
      <div className="w-full max-w-5xl h-[90vh] bg-background shadow-lg overflow-y-auto rounded-lg m-4">
        <div className="container max-w-4xl py-8">
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    {onBack && (
                      <Button variant="ghost" size="sm" onClick={onBack}>
                        <ArrowLeft className="h-4 w-4" />
                      </Button>
                    )}
                    <CardTitle className="text-2xl">{resposta.formulario?.titulo}</CardTitle>
                  </div>
                  {resposta.formulario?.descricao && (
                    <CardDescription className="mt-2 text-base">
                      {resposta.formulario.descricao}
                    </CardDescription>
                  )}
                </div>
                <Button variant="ghost" onClick={onClose}>✕</Button>
              </div>

              <div className="flex items-center gap-4 mt-4 text-sm">
                {getStatusBadge(resposta.status)}
                <Separator orientation="vertical" className="h-4" />
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  <span>{resposta.respondente?.name || 'Anônimo'}</span>
                </div>
                <Separator orientation="vertical" className="h-4" />
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <span>{format(new Date(resposta.data_inicio), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}</span>
                </div>
              </div>

              {resposta.latitude && resposta.longitude && (
                <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
                  <MapPin className="h-4 w-4" />
                  <span>Lat: {resposta.latitude.toFixed(6)}, Lng: {resposta.longitude.toFixed(6)}</span>
                </div>
              )}
            </CardHeader>

            <CardContent>
              <div className="space-y-6">
                {campos.length === 0 ? (
                  <p className="text-center text-muted-foreground">Nenhum campo preenchido</p>
                ) : (
                  campos
                    .sort((a: any, b: any) => a.campo.ordem - b.campo.ordem)
                    .map((campoResposta: any) => (
                      <div key={campoResposta.id} className="space-y-2">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-medium">{campoResposta.campo.label}</h4>
                            {campoResposta.campo.descricao && (
                              <p className="text-sm text-muted-foreground">{campoResposta.campo.descricao}</p>
                            )}
                          </div>
                          <Badge variant="outline">{campoResposta.campo.tipo_campo}</Badge>
                        </div>
                        <div className="pl-4 border-l-2 border-muted">
                          {renderValorCampo(campoResposta)}
                        </div>
                      </div>
                    ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
